'use client'

import { cva } from 'class-variance-authority'

import ChipSecondaryLabel from '~/core/ui/ChipSecondaryLabel'
import type { LucideIconName } from '~/core/ui/IconWrapper'
import IconWrapper from '~/core/ui/IconWrapper'
import { Tooltip } from '~/core/ui/Tooltip'
import { cn, truncateTextWithDot } from '~/core/ui/utils'
import { isHTML } from '~/core/utilities/common'

const chipVariants = cva(
  'focus:ring-primary-300 dark:focus:ring-primary-700 flex items-center rounded border border-solid border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-1 disabled:pointer-events-none disabled:border-gray-200 disabled:text-gray-400 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:disabled:border-gray-700 dark:disabled:text-gray-700',
  {
    variants: {
      variant: {
        4: 'pl-3',
        3: 'pl-2.5',
        2: 'pl-2',
        1: 'pl-1.5'
      },
      size: {
        lg: 'h-8 py-1.5 pr-3',
        md: 'h-6 py-0.5 pr-2.5',
        sm: 'h-5 py-px pr-2'
      }
    },
    defaultVariants: {
      variant: 4,
      size: 'lg'
    }
  }
)

const chipLabelVariants = cva('', {
  variants: {
    disabled: {
      disabled:
        'pointer-events-none border-gray-200 text-gray-400 dark:border-gray-700 dark:text-gray-700',
      default:
        'focus:ring-primary-300 dark:focus:ring-primary-700 border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-1 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800'
    },
    size: {
      lg: 'text-sm',
      md: 'text-sm',
      sm: 'text-xs'
    }
  },
  defaultVariants: {
    size: 'lg',
    disabled: 'default'
  }
})

const getSuggestionChipPaddingCondition = ({
  size = 'lg',
  type = 'default'
}: {
  size?: 'lg' | 'md' | 'sm'
  type?: 'default' | 'icon-leading'
}) => {
  if (size === 'lg' && type === 'default') return 4

  if (
    (size === 'md' && type === 'default') ||
    (size === 'lg' && type === 'icon-leading')
  ) {
    return 3
  }

  if (
    (size === 'sm' && type === 'default') ||
    (size === 'md' && type === 'icon-leading')
  ) {
    return 2
  }

  if (size === 'sm' && type === 'icon-leading') return 1

  return 4
}

const suggestionChipDefaultSizeIcon: { [key: string]: number } = {
  lg: 16,
  md: 14,
  sm: 12
}

export type TooltipContentKey = 'label' | 'description' | 'secondaryLabel'

export const getDescriptionText = (value?: string) => {
  if (!value) return ''

  const textWithLineBreaks = value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]*>/g, '')

  if (typeof document === 'undefined') {
    return textWithLineBreaks.trim()
  }

  const textarea = document.createElement('textarea')
  textarea.innerHTML = textWithLineBreaks

  return textarea.value.trim()
}


interface SuggestionChipsGeneralProps {
  size?: 'lg' | 'md' | 'sm'
  type?: 'default' | 'icon-leading'
  isDisabled?: boolean
  isRemovable?: boolean
  classNameChip?: string
  startIconMenus?: LucideIconName
  startIconMenusClassName?: string
  startIconMenusFill?: string
  classNameChipText?: string
  showTooltip?: boolean
  tooltipContent?: TooltipContentKey[]
  maxLength?: number
  showSecondaryLabel?: boolean
  secondaryLabelTitle?: React.ReactNode
  renderSecondaryLabelTooltip?: (secondaryLabel: string) => React.ReactNode
  secondaryLabelClassName?: string
  secondaryLabelSeparatorClassName?: string
  secondaryLabelTooltipClassName?: string
  tooltipLabelSuffix?: React.ReactNode
  tooltipLabelSuffixClassName?: string
}

type SuggestionChipsProps = SuggestionChipsGeneralProps & {
  label?: string
  labelSuffix?: React.ReactNode
  secondaryLabel?: string
  iconLeading?: LucideIconName
  index?: number
  onClick?: (e: any) => void
  description?: string
}

type SuggestionChipsSelectProps = SuggestionChipsGeneralProps & {
  source: Array<SuggestionChipsProps>
  maxItems?: number
  startIconMaxItem?: boolean
  startIconMenus?: LucideIconName
  className?: string
  clickableOnMore?: boolean
  getTooltipLabelSuffix?: (item: SuggestionChipsProps) => React.ReactNode
}

const renderOverflowChipLabel = (suggestionChip: SuggestionChipsProps) => {
  const labelNode =
    suggestionChip.label && isHTML(suggestionChip.label) ? (
      <span dangerouslySetInnerHTML={{ __html: String(suggestionChip.label) }} />
    ) : (
      suggestionChip.label
    )

  if (!suggestionChip.secondaryLabel) return labelNode

  return (
    <span className="inline-flex items-center text-gray-100">
      <span className="font-medium">{labelNode}</span>
      <ChipSecondaryLabel
        label={suggestionChip.secondaryLabel}
        className={cn('text-gray-100', suggestionChip.secondaryLabelClassName)}
        separatorClassName={suggestionChip.secondaryLabelSeparatorClassName}
      />
    </span>
  )
}

const SuggestionChipsItem = ({
  index,
  maxLength,
  size = 'lg',
  type = 'default',
  isRemovable = false,
  showTooltip = false,
  tooltipContent = ['label'],
  description,
  label,
  secondaryLabel,
  showSecondaryLabel = true,
  secondaryLabelTitle,
  renderSecondaryLabelTooltip,
  secondaryLabelClassName,
  secondaryLabelSeparatorClassName,
  secondaryLabelTooltipClassName,
  tooltipLabelSuffix,
  labelSuffix,
  tooltipLabelSuffixClassName,
  isDisabled = false,
  onClick,
  classNameChip = '',
  startIconMenus,
  startIconMenusClassName = '',
  startIconMenusFill,
  classNameChipText = ''
}: SuggestionChipsProps) => {
  const isLabelTruncated = Boolean(
    label && maxLength && label.length > maxLength
  )
  const effectiveLabelSuffix = tooltipLabelSuffix ?? labelSuffix

  const renderTooltipContent = () => {
    if (showTooltip) {
      const showLabel = tooltipContent.includes('label')
      const showDescription =
        tooltipContent.includes('description') || Boolean(description)
      const descriptionText = getDescriptionText(description)
      const shouldShowDescription = showDescription && Boolean(descriptionText)
      const showSecondaryLabelInTooltip =
        Boolean(secondaryLabel) &&
        (tooltipContent.includes('secondaryLabel') ||
          tooltipContent.includes('label'))

      if (showLabel || shouldShowDescription || showSecondaryLabelInTooltip) {
        return (
          <div className="flex flex-col text-gray-100">
            {showLabel && (
              <div>
                <span className="font-medium">
                  {label && isHTML(label) ? (
                    <span dangerouslySetInnerHTML={{ __html: label }} />
                  ) : (
                    label
                  )}
                </span>
                {effectiveLabelSuffix && (
                  <span className={cn('ml-1 font-medium', tooltipLabelSuffixClassName)}>
                    {effectiveLabelSuffix}
                  </span>
                )}
              </div>
            )}
            {showSecondaryLabelInTooltip && secondaryLabel && (
              <span className={secondaryLabelTooltipClassName}>
                {renderSecondaryLabelTooltip ? (
                  renderSecondaryLabelTooltip(secondaryLabel)
                ) : secondaryLabelTitle ? (
                  <>
                    <span>
                      {secondaryLabelTitle}
                      {typeof secondaryLabelTitle === 'string' &&
                      secondaryLabelTitle.trim().endsWith(':')
                        ? ' '
                        : ': '}
                    </span>
                    <span>{secondaryLabel}</span>
                  </>
                ) : (
                  secondaryLabel
                )}
              </span>
            )}
            {shouldShowDescription && (
              <span className="description-content whitespace-pre-line">
                {descriptionText}
              </span>
            )}
          </div>
        )
      }
    }

    // Single tooltip for truncated labels when showTooltip is off
    if (isLabelTruncated && label) {
      return (
        <span className="inline-flex items-center">
          {isHTML(label) ? (
            <span dangerouslySetInnerHTML={{ __html: label }} />
          ) : (
            label
          )}
          <ChipSecondaryLabel
            label={secondaryLabel}
            className={secondaryLabelClassName}
            separatorClassName={secondaryLabelSeparatorClassName}
          />
        </span>
      )
    }

    return null
  }

  const chipTooltipContent = renderTooltipContent()


  const chipLabelClassName = cn(
    'line-clamp-1 break-all',
    chipLabelVariants({
      size,
      disabled: isDisabled ? 'disabled' : 'default',
      className: classNameChip
    })
  )

  const renderLabel = () => {
    if (!label) return null

    if (isLabelTruncated && maxLength) {
      if (isHTML(label)) {
        return (
          <span
            className={chipLabelClassName}
            dangerouslySetInnerHTML={{
              __html: truncateTextWithDot(
                label,
                maxLength + (label?.match(/<mark>/gim)?.length || 0) * 13
              )
            }}
          />
        )
      }

      return (
        <span className={chipLabelClassName}>
          {truncateTextWithDot(label, maxLength)}
        </span>
      )
    }

    if (isHTML(label)) {
      return (
        <span
          className={chipLabelClassName}
          dangerouslySetInnerHTML={{ __html: label }}
        />
      )
    }

    return <span className={chipLabelClassName}>{label}</span>
  }


  return (
    <Tooltip
      isCustomArrow
      align='start'
      content={chipTooltipContent}>
      <button
        type="button"
        className={cn(
          'line-clamp-1',
          chipVariants({
            variant: getSuggestionChipPaddingCondition({ size, type }),
            size,
            className: classNameChip
          })
        )}
        disabled={isDisabled}
        onClick={(e) => {
          if (onClick) {
            onClick(e)
          }
        }}>
        <>
          {startIconMenus && (
            <IconWrapper
              name={startIconMenus}
              size={suggestionChipDefaultSizeIcon[size]}
              fill={startIconMenusFill}
              className={cn('mr-1 flex-none', startIconMenusClassName)}
            />
          )}
          {renderLabel()}
          {showSecondaryLabel && (
            <ChipSecondaryLabel
              label={secondaryLabel}
              className={cn('line-clamp-1 break-all', secondaryLabelClassName)}
              separatorClassName={secondaryLabelSeparatorClassName}
            />
          )}
          {isRemovable && (
            <span className="ml-1.5 cursor-pointer">
              <IconWrapper
                name="X"
                size={suggestionChipDefaultSizeIcon[size]}
                className={
                  isDisabled === false
                    ? 'text-gray-700 dark:text-gray-300'
                    : 'text-gray-400 dark:text-gray-700'
                }
              />
            </span>
          )}
        </>
      </button>
    </Tooltip>
  )
}

const SuggestionChips = (props: SuggestionChipsSelectProps) => {
  const {
    maxItems = 0,
    className,
    source = [],
    showSecondaryLabel = true,
    secondaryLabelTitle,
    renderSecondaryLabelTooltip,
    secondaryLabelClassName,
    secondaryLabelSeparatorClassName,
    secondaryLabelTooltipClassName,
    tooltipLabelSuffix,
    tooltipLabelSuffixClassName,
    getTooltipLabelSuffix,
    ...otherProps
  } = props
  return (
    <div className={`-mt-2 flex flex-wrap items-center ${className || ''}`}>
      {(maxItems > 0 ? source.slice(0, maxItems) : source).map(
        (suggestionChip, index) => {
          return (
            <div
              key={`suggestion-chip-${index}`}
              className="mt-2 mr-2 max-w-full flex-none last:mr-0">
              <SuggestionChipsItem
                index={index}
                showSecondaryLabel={
                  suggestionChip.showSecondaryLabel ?? showSecondaryLabel
                }
                secondaryLabelTitle={secondaryLabelTitle}
                renderSecondaryLabelTooltip={renderSecondaryLabelTooltip}
                secondaryLabelClassName={secondaryLabelClassName}
                secondaryLabelSeparatorClassName={secondaryLabelSeparatorClassName}
                secondaryLabelTooltipClassName={secondaryLabelTooltipClassName}
                tooltipLabelSuffix={
                  suggestionChip.tooltipLabelSuffix ??
                  getTooltipLabelSuffix?.(suggestionChip) ??
                  tooltipLabelSuffix
                }
                tooltipLabelSuffixClassName={
                  suggestionChip.tooltipLabelSuffixClassName ??
                  tooltipLabelSuffixClassName
                }
                {...otherProps}
                {...suggestionChip}
              />
            </div>
          )
        }
      )}
      {maxItems > 0 && maxItems < source.length && (
        <Tooltip
          align="center"
          content={source.slice(maxItems).map((suggestionChip, index) => (
            <div
              key={`suggestion-tooltip-${index}`}
              className={
                suggestionChip.onClick ? 'cursor-pointer hover:underline' : ''
              }
              onClick={(e) => {
                if (suggestionChip.onClick) {
                  suggestionChip.onClick(e)
                }
              }}>
              {renderOverflowChipLabel({
                secondaryLabelClassName,
                secondaryLabelSeparatorClassName,
                ...suggestionChip
              })}
            </div>
          ))}
          position="top">
          <div className="mt-2 mr-2 max-w-full flex-none last:mr-0">
            <SuggestionChipsItem
              index={9999}
              {...otherProps}
              label={`+${source.length - maxItems}`}
              secondaryLabel={undefined}
            />
          </div>
        </Tooltip>
      )}
    </div>
  )
}

const SuggestionInlineChips = (props: SuggestionChipsSelectProps) => {
  const {
    maxItems = 0,
    className,
    source = [],
    startIconMaxItem,
    startIconMenus,
    clickableOnMore,
    showSecondaryLabel = true,
    secondaryLabelTitle,
    renderSecondaryLabelTooltip,
    secondaryLabelClassName,
    secondaryLabelSeparatorClassName,
    secondaryLabelTooltipClassName,
    tooltipLabelSuffix,
    tooltipLabelSuffixClassName,
    getTooltipLabelSuffix,
    ...otherProps
  } = props
  const onClickButtonMore =
    clickableOnMore && source.length > 0 ? source?.[0]?.onClick : null
  return (
    <>
      {(maxItems > 0 ? source.slice(0, maxItems) : source).map(
        (suggestionChip, index) => {
          return (
            <div
              key={`suggestion-chip-${index}`}
              className={cn(
                'mt-2 mr-2 max-w-full flex-none last:mr-0',
                className
              )}>
              <SuggestionChipsItem
                index={index}
                showSecondaryLabel={
                  suggestionChip.showSecondaryLabel ?? showSecondaryLabel
                }
                secondaryLabelTitle={secondaryLabelTitle}
                renderSecondaryLabelTooltip={renderSecondaryLabelTooltip}
                secondaryLabelClassName={secondaryLabelClassName}
                secondaryLabelSeparatorClassName={secondaryLabelSeparatorClassName}
                secondaryLabelTooltipClassName={secondaryLabelTooltipClassName}
                tooltipLabelSuffix={
                  suggestionChip.tooltipLabelSuffix ??
                  getTooltipLabelSuffix?.(suggestionChip) ??
                  tooltipLabelSuffix
                }
                tooltipLabelSuffixClassName={
                  suggestionChip.tooltipLabelSuffixClassName ??
                  tooltipLabelSuffixClassName
                }
                {...otherProps}
                {...suggestionChip}
              />
            </div>
          )
        }
      )}
      {maxItems > 0 && maxItems < source.length && (
        <Tooltip
          align="center"
          classNameAsChild="flex-none"
          sideOffset={0}
          content={source.slice(maxItems).map((suggestionChip, index) => (
            <div
              key={`suggestion-tooltip-${index}`}
              className={
                suggestionChip.onClick ? 'cursor-pointer hover:underline' : ''
              }
              onClick={(e) => {
                if (suggestionChip.onClick) {
                  suggestionChip.onClick(e)
                }
              }}>
              {renderOverflowChipLabel({
                secondaryLabelClassName,
                secondaryLabelSeparatorClassName,
                ...suggestionChip
              })}
            </div>
          ))}
          position="top">
          <div className="mt-2 mr-2 max-w-full flex-none last:mr-0">
            <SuggestionChipsItem
              {...otherProps}
              onClick={(e) => onClickButtonMore && onClickButtonMore(e)}
              index={9999}
              label={`+${source.length - maxItems}`}
              secondaryLabel={undefined}
              startIconMenus={startIconMaxItem ? startIconMenus : undefined}
            />
          </div>
        </Tooltip>
      )}
    </>
  )
}

export { SuggestionChips, SuggestionChipsItem, SuggestionInlineChips }
export type {
  SuggestionChipsGeneralProps,
  SuggestionChipsProps,
  SuggestionChipsSelectProps
}
